import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Error as ErrorIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatReminderDisplay } from '../../utils/reminderUtils';
import { enqueueSnackbar } from 'notistack';

import DataTable from '../common/DataTable';
import QueueJobStatusChip from '../common/QueueJobStatusChip';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import type { TableColumn } from '../../types/common.types';
import type { QueueJob, JobStatus, QueueFilters, QueueStats, ScadenzaReminder } from '../../types/queue.types';
import type { ReminderJob, ReminderJobsStats, ReminderJobStatus } from '../../types/reminderJob.types';
import { queueService } from '../../services/queueService';
import { reminderJobsService } from '../../services/reminderJobsService';

interface QueueDashboardProps {
  autoRefreshInterval?: number; // in seconds, default 30
}

const QueueDashboard: React.FC<QueueDashboardProps> = ({
  autoRefreshInterval = 30
}) => {
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [scadenzeReminders, setScadenzeReminders] = useState<ScadenzaReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<QueueFilters>({});
  const [sortField, setSortField] = useState<string>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'scadenze' | 'reminderJobs'>('reminderJobs');

  // ReminderJobs from MongoDB
  const [reminderJobs, setReminderJobs] = useState<ReminderJob[]>([]);
  const [reminderJobsStats, setReminderJobsStats] = useState<ReminderJobsStats | null>(null);
  const [reminderJobsTotal, setReminderJobsTotal] = useState(0);

  // Client-side sorting for Scadenze
  const sortedScadenzeReminders = React.useMemo(() => {
    if (!scadenzeReminders.length) return [];
    
    return [...scadenzeReminders].sort((a, b) => {
      let aValue: any = a[sortField as keyof ScadenzaReminder];
      let bValue: any = b[sortField as keyof ScadenzaReminder];

      // Handle nested/special fields
      if (sortField === 'cliente') {
        // cliente is string in ScadenzaReminder (after backend fix)
        aValue = a.cliente || '';
        bValue = b.cliente || '';
      } else if (sortField === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (sortField === 'dataScadenza') {
        aValue = new Date(a.dataScadenza).getTime();
        bValue = new Date(b.dataScadenza).getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }

      // Numeric/Date sort
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [scadenzeReminders, sortField, sortOrder]);

  const fetchData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sort: `${sortOrder === 'desc' ? '-' : ''}${sortField}`,
        ...filters
      };

      // Fetch based on active tab
      if (activeTab === 'reminderJobs') {
        // Fetch ReminderJobs from MongoDB
        const reminderSort = sortField === 'timestamp' ? '-dataEsecuzionePrevista' : `${sortOrder === 'desc' ? '-' : ''}${sortField}`;
        const [reminderJobsRes, reminderStatsRes] = await Promise.all([
          reminderJobsService.getAll({
            page: page + 1,
            limit: rowsPerPage,
            sort: reminderSort
          }),
          reminderJobsService.getStats()
        ]);
        setReminderJobs(reminderJobsRes.data);
        setReminderJobsTotal(reminderJobsRes.total);
        setReminderJobsStats(reminderStatsRes);
      } else if (activeTab === 'scadenze') {
        const [jobsRes, statsRes, scadenzeRes] = await Promise.all([
          queueService.getJobs(params),
          queueService.getStats(),
          queueService.getScadenzeReminders()
        ]);
        setJobs(jobsRes.data);
        setTotal(jobsRes.pagination.total);
        setStats(statsRes);
        if (scadenzeRes && Array.isArray(scadenzeRes)) {
          setScadenzeReminders(scadenzeRes);
        } else {
          setScadenzeReminders([]);
        }
      } else {
        // Jobs tab
        const [jobsRes, statsRes] = await Promise.all([
          queueService.getJobs(params),
          queueService.getStats()
        ]);
        setJobs(jobsRes.data);
        setTotal(jobsRes.pagination.total);
        setStats(statsRes);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il caricamento dei dati');
      enqueueSnackbar('Errore durante il caricamento dei job della queue', { variant: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData(false);
  };

  const handleStatusChange = (status: JobStatus | '') => {
    setFilters({ ...filters, status: status || undefined });
    setPage(0);
  };

  const handleSearchChange = (search: string) => {
    setFilters({ ...filters, search: search || undefined });
    setPage(0);
  };

  const handleJobTypeChange = (jobType: string) => {
    setFilters({ ...filters, jobType: jobType || undefined });
    setPage(0);
  };

  const handleTestFilterChange = (testFilter: string) => {
    const newFilters = { ...filters };
    // Reset test filters
    delete newFilters.isTestOnly;
    delete newFilters.isProductionOnly;
    
    if (testFilter === 'test') {
      newFilters.isTestOnly = true;
    } else if (testFilter === 'production') {
      newFilters.isProductionOnly = true;
    }
    
    setFilters(newFilters);
    setPage(0);
  };

  const handleViewDetails = (job: QueueJob) => {
    setSelectedJob(job);
    setDetailDialogOpen(true);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleDebugTest = async () => {
    try {
      const response = await fetch('/api/scheduler/debug/test-scadenze', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      const data = await response.json();
      console.log('Test scadenze debug:', data);
      enqueueSnackbar(`Trovate ${data.result.testScadenzeCount} scadenze di test`, { variant: 'info' });
    } catch (error) {
      console.error('Debug failed:', error);
      enqueueSnackbar('Errore nel debug test', { variant: 'error' });
    }
  };

  const handleTriggerTest = async () => {
    try {
      const response = await fetch('/api/scheduler/trigger/test-reminders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      await response.json();
      enqueueSnackbar('Test reminder triggerato con successo', { variant: 'success' });
      // Refresh data dopo il trigger
      setTimeout(() => fetchData(false), 1000);
    } catch (error) {
      console.error('Trigger failed:', error);
      enqueueSnackbar('Errore nel trigger test', { variant: 'error' });
    }
  };

  const handleProcessReminderJobs = async () => {
    try {
      const response = await fetch('/api/scheduler/trigger/process-reminder-jobs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        enqueueSnackbar(
          `Processati ${data.result.processed} job, ${data.result.failed} falliti`,
          { variant: data.result.failed > 0 ? 'warning' : 'success' }
        );
      } else {
        enqueueSnackbar('Errore nel processing dei reminder jobs', { variant: 'error' });
      }
      // Refresh data dopo il processing
      setTimeout(() => fetchData(false), 1000);
    } catch (error) {
      console.error('Process reminder jobs failed:', error);
      enqueueSnackbar('Errore nel processing dei reminder jobs', { variant: 'error' });
    }
  };

  const handleForceExecuteJob = async (jobId: string) => {
    try {
      const response = await fetch(`/api/scheduler/trigger/force-execute-job/${jobId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        enqueueSnackbar(data.message || 'Job eseguito con successo', { variant: 'success' });
      } else {
        enqueueSnackbar(data.message || 'Errore nell\'esecuzione del job', { variant: 'error' });
      }
      // Refresh data dopo l'esecuzione
      setTimeout(() => fetchData(false), 1000);
    } catch (error) {
      console.error('Force execute job failed:', error);
      enqueueSnackbar('Errore nell\'esecuzione forzata del job', { variant: 'error' });
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
    }, autoRefreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefreshInterval, page, rowsPerPage, filters, sortField, sortOrder]);

  // Initial load and dependency changes
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters, sortField, sortOrder, activeTab]);

  // Statistics cards
  const renderStatsCards = () => {
    if (!stats) return null;

    const statsItems = [
      { label: 'In Attesa', value: stats.waiting, color: '#FFA726' },
      { label: 'Attivi', value: stats.active, color: '#66BB6A' },
      { label: 'Completati', value: stats.completed, color: '#42A5F5' },
      { label: 'Falliti', value: stats.failed, color: '#EF5350' },
      { label: 'Ritardati', value: stats.delayed, color: '#AB47BC' }
    ];

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {statsItems.map((item) => (
          <Box key={item.label} sx={{ width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(20% - 16px)' } }}>
            <Card sx={{ minHeight: 100 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: item.color, fontWeight: 'bold' }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    );
  };

  // Scadenze Reminders table columns
  const scadenzeColumns: TableColumn<ScadenzaReminder>[] = [
    {
      id: 'cliente',
      label: 'Cliente',
      sortable: true,
      format: (_, scadenza) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {scadenza.cliente}
        </Typography>
      )
    },
    {
      id: 'descrizione', 
      label: 'Descrizione',
      format: (_, scadenza) => (
        <Typography variant="body2">
          {scadenza.descrizione}
        </Typography>
      )
    },
    {
      id: 'fatturaNumero',
      label: 'Fattura',
      format: (_, scadenza) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {scadenza.fatturaNumero}
        </Typography>
      )
    },
    {
      id: 'dataScadenza',
      label: 'Data Scadenza',
      sortable: true,
      format: (_, scadenza) => (
        <Typography variant="body2">
          {format(new Date(scadenza.dataScadenza), 'dd/MM/yyyy HH:mm', { locale: it })}
        </Typography>
      )
    },
    {
      id: 'createdAt',
      label: 'Creata il',
      sortable: true,
      format: (_, scadenza) => (
        <Typography variant="body2" title={scadenza.createdAt ? format(new Date(scadenza.createdAt), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}>
          {scadenza.createdAt 
            ? formatDistanceToNow(new Date(scadenza.createdAt), { addSuffix: true, locale: it })
            : '-'}
        </Typography>
      )
    },
    {
      id: 'giorniPreavviso',
      label: 'Preavviso',
      format: (_, scadenza) => (
        <Chip
          label={formatReminderDisplay(scadenza.giorniPreavviso, true)}
          size="small"
          color={scadenza.giorniPreavviso === 0.0007 ? 'warning' : 'default'}
        />
      )
    },
    {
      id: 'jobStatus',
      label: 'Stato Job',
      format: (_, scadenza) => {
        if (scadenza.hasFailedJob) {
          return <Chip label="Fallito" color="error" size="small" />;
        } else if (scadenza.hasCompletedJob) {
          return <Chip label="Completato" color="success" size="small" />;
        } else if (scadenza.hasActiveJob) {
          return <Chip label="Attivo" color="primary" size="small" />;
        } else if (scadenza.hasScheduledJob) {
          return <Chip label="Programmato" color="info" size="small" />;
        } else {
          return <Chip label="Nessun Job" color="default" size="small" />;
        }
      }
    },
    {
      id: 'jobs',
      label: 'Jobs (#)',
      format: (_, scadenza) => (
        <Badge badgeContent={scadenza.jobs.length} color="primary">
          <Typography variant="body2">
            {scadenza.jobs.length > 0 ? scadenza.jobs.length : '0'}
          </Typography>
        </Badge>
      )
    },
    {
      id: 'reminderStatus',
      label: 'Stato Reminder',
      format: (_, scadenza) => {
        if (scadenza.reminderOverdue) {
          return <Chip label="In Ritardo" color="error" size="small" />;
        } else if (scadenza.reminderDue) {
          return <Chip label="Da Inviare" color="warning" size="small" />;
        } else if (scadenza.inviataNotifica) {
          return <Chip label="Inviata" color="success" size="small" />;
        } else {
          return <Chip label="In Attesa" color="default" size="small" />;
        }
      }
    }
  ];

  const renderScadenzeStats = () => {
    if (!Array.isArray(scadenzeReminders)) {
      return null;
    }

    const activeReminders = scadenzeReminders.filter(s => s.hasActiveJob).length;
    const scheduledReminders = scadenzeReminders.filter(s => s.hasScheduledJob).length;
    const failedReminders = scadenzeReminders.filter(s => s.hasFailedJob).length;
    const completedReminders = scadenzeReminders.filter(s => s.hasCompletedJob).length;
    const overdueReminders = scadenzeReminders.filter(s => s.reminderOverdue).length;

    const scadenzeStats = [
      { label: 'Job Attivi', value: activeReminders, color: '#66BB6A' },
      { label: 'Programmati', value: scheduledReminders, color: '#42A5F5' },
      { label: 'Falliti', value: failedReminders, color: '#EF5350' },
      { label: 'Completati', value: completedReminders, color: '#26A69A' },
      { label: 'In Ritardo', value: overdueReminders, color: '#FF7043' }
    ];

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {scadenzeStats.map((item) => (
          <Box key={item.label} sx={{ width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(20% - 16px)' } }}>
            <Card sx={{ minHeight: 100 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: item.color, fontWeight: 'bold' }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    );
  };

  // ReminderJobs statistics cards (from MongoDB)
  const renderReminderJobsStats = () => {
    if (!reminderJobsStats) return null;

    const { perStato } = reminderJobsStats;
    const statsItems = [
      { label: 'In Attesa', value: perStato.pending, color: '#FFA726' },
      { label: 'In Esecuzione', value: perStato.processing, color: '#66BB6A' },
      { label: 'Completati', value: perStato.completed, color: '#42A5F5' },
      { label: 'Falliti', value: perStato.failed, color: '#EF5350' },
      { label: 'Annullati', value: perStato.cancelled, color: '#9E9E9E' }
    ];

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {statsItems.map((item) => (
          <Box key={item.label} sx={{ width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(20% - 16px)' } }}>
            <Card sx={{ minHeight: 100 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: item.color, fontWeight: 'bold' }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    );
  };

  // ReminderJobs table columns (MongoDB)
  const reminderJobsColumns: TableColumn<ReminderJob>[] = [
    {
      id: 'id',
      label: 'ID',
      sortable: false,
      format: (_, job) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {job.id}
        </Typography>
      )
    },
    {
      id: 'cliente',
      label: 'Cliente',
      sortable: false,
      format: (_, job) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {job.cliente ? `${job.cliente.cognome} ${job.cliente.nome}`.trim() : '-'}
        </Typography>
      )
    },
    {
      id: 'dataEsecuzionePrevista',
      label: 'Esecuzione Prevista',
      sortable: true,
      format: (_, job) => (
        <Box>
          <Typography variant="body2">
            {format(new Date(job.dataEsecuzionePrevista), 'dd/MM/yyyy HH:mm', { locale: it })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(new Date(job.dataEsecuzionePrevista), { addSuffix: true, locale: it })}
          </Typography>
        </Box>
      )
    },
    {
      id: 'giorniPreavviso',
      label: 'Preavviso',
      format: (_, job) => (
        <Chip
          label={formatReminderDisplay(job.payload?.giorniPreavviso || 0, true)}
          size="small"
          color={job.payload?.giorniPreavviso === 0.0007 ? 'warning' : 'default'}
        />
      )
    },
    {
      id: 'stato',
      label: 'Stato',
      sortable: true,
      format: (_, job) => {
        const statusConfig: Record<string, { color: 'default' | 'warning' | 'success' | 'error' | 'info', label: string }> = {
          pending: { color: 'warning', label: 'In Attesa' },
          processing: { color: 'info', label: 'In Esecuzione' },
          completed: { color: 'success', label: 'Completato' },
          failed: { color: 'error', label: 'Fallito' },
          cancelled: { color: 'default', label: 'Annullato' }
        };
        const config = statusConfig[job.stato] || { color: 'default', label: job.stato };
        return <Chip label={config.label} color={config.color} size="small" />;
      }
    },
    {
      id: 'priorita',
      label: 'Priorita',
      format: (_, job) => {
        const priorityConfig: Record<string, { color: 'default' | 'warning' | 'error' | 'info', label: string }> = {
          low: { color: 'default', label: 'Bassa' },
          normal: { color: 'info', label: 'Normale' },
          high: { color: 'warning', label: 'Alta' },
          urgent: { color: 'error', label: 'Urgente' }
        };
        const config = priorityConfig[job.priorita] || { color: 'default', label: job.priorita };
        return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
      }
    },
    {
      id: 'tentativi',
      label: 'Tentativi',
      format: (_, job) => (
        <Typography variant="body2">
          {job.tentativiEffettuati} / {job.tentativiMassimi}
        </Typography>
      )
    },
    {
      id: 'createdAt',
      label: 'Creato',
      sortable: true,
      format: (_, job) => (
        <Typography variant="body2" title={format(new Date(job.createdAt), 'dd/MM/yyyy HH:mm', { locale: it })}>
          {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true, locale: it })}
        </Typography>
      )
    },
    {
      id: 'actions',
      label: 'Azioni',
      format: (_, job) => (
        <Box>
          {job.stato === 'pending' && (
            <Tooltip title="Invia manualmente">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleForceExecuteJob(job.id)}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {job.stato === 'failed' && (
            <Tooltip title="Job fallito">
              <IconButton size="small" color="error">
                <ErrorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  // Table columns
  const columns: TableColumn<QueueJob>[] = [
    {
      id: 'id',
      label: 'ID',
      sortable: true,
      format: (_, job) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {String(job.id).slice(0, 8)}...
        </Typography>
      )
    },
    {
      id: 'name',
      label: 'Nome Job',
      sortable: true,
      format: (_, job) => (
        <Box>
          <Typography variant="body2">
            {job.name}
          </Typography>
          {job.isTestJob && (
            <Chip
              label={job.displayLabel || 'TEST'}
              size="small"
              color="warning"
              sx={{ mt: 0.5, fontSize: '0.75rem' }}
            />
          )}
        </Box>
      )
    },
    {
      id: 'status',
      label: 'Stato',
      sortable: true,
      format: (_, job) => <QueueJobStatusChip status={job.status} />
    },
    {
      id: 'progress',
      label: 'Progresso',
      format: (_, job) => (
        <Box sx={{ minWidth: 80 }}>
          <LinearProgress
            variant="determinate"
            value={job.progress || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
            {job.progress || 0}%
          </Typography>
        </Box>
      )
    },
    {
      id: 'attemptsMade',
      label: 'Tentativi',
      sortable: true,
      format: (_, job) => (
        <Typography variant="body2">
          {job.attemptsMade} / {job.opts?.attempts || 1}
        </Typography>
      )
    },
    {
      id: 'timestamp',
      label: 'Creato',
      sortable: true,
      format: (_, job) => (
        <Typography variant="body2">
          {job.timestamp && format(new Date(job.timestamp), 'dd/MM/yyyy HH:mm', { locale: it })}
        </Typography>
      )
    },
    {
      id: 'actions',
      label: 'Azioni',
      format: (_, job) => (
        <Box>
          <Tooltip title="Visualizza dettagli">
            <IconButton size="small" onClick={() => handleViewDetails(job)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {job.status === 'failed' && (
            <Tooltip title="Job fallito">
              <IconButton size="small" color="error">
                <ErrorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  if (loading) {
    return <LoadingSpinner message="Caricamento queue dashboard..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Queue Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleDebugTest}
            size="small"
          >
            Debug Test
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={handleTriggerTest}
            size="small"
          >
            Trigger Test
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Aggiornamento...' : 'Aggiorna'}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Box sx={{ mb: 3 }}>
            <ErrorAlert
              title="Errore nel caricamento"
              error={error}
              onRetry={() => fetchData()}
            />
        </Box>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            label={
              <Badge badgeContent={reminderJobsStats?.perStato.pending || 0} color="warning" max={99}>
                <Box sx={{ pr: 2 }}>Reminder Jobs</Box>
              </Badge>
            }
            value="reminderJobs"
          />
          <Tab label="Jobs Queue (Redis)" value="jobs" />
          <Tab label="Scadenze Monitoring" value="scadenze" />
        </Tabs>
      </Box>

      {/* Statistics Cards */}
      {activeTab === 'reminderJobs' && renderReminderJobsStats()}
      {activeTab === 'jobs' && renderStatsCards()}
      {activeTab === 'scadenze' && renderScadenzeStats()}

      {/* Filters - Show only for jobs tab */}
      {activeTab === 'jobs' && (
        <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filtri
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => handleStatusChange(e.target.value as JobStatus | '')}
                  label="Stato"
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="waiting">In Attesa</MenuItem>
                  <MenuItem value="active">Attivo</MenuItem>
                  <MenuItem value="completed">Completato</MenuItem>
                  <MenuItem value="failed">Fallito</MenuItem>
                  <MenuItem value="delayed">Ritardato</MenuItem>
                  <MenuItem value="paused">In Pausa</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <TextField
                fullWidth
                label="Tipo Job"
                value={filters.jobType || ''}
                onChange={(e) => handleJobTypeChange(e.target.value)}
                placeholder="es. send-reminder"
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.isTestOnly ? 'test' : filters.isProductionOnly ? 'production' : ''}
                  onChange={(e) => handleTestFilterChange(e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="test">Solo Test</MenuItem>
                  <MenuItem value="production">Solo Produzione</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' } }}>
              <TextField
                fullWidth
                label="Ricerca"
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Cerca nei dati del job..."
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Tables */}
      <Card>
        <CardContent>
          {activeTab === 'reminderJobs' && (
            <DataTable
              data={reminderJobs}
              columns={reminderJobsColumns}
              loading={refreshing}
              page={page}
              rowsPerPage={rowsPerPage}
              total={reminderJobsTotal}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortOrder}
              emptyMessage="Nessun reminder job trovato. Crea una scadenza per generare i reminder."
              aria-label="Reminder Jobs Table"
            />
          )}
          {activeTab === 'jobs' && (
            <DataTable
              data={jobs}
              columns={columns}
              loading={refreshing}
              page={page}
              rowsPerPage={rowsPerPage}
              total={total}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortOrder}
              emptyMessage="Nessun job trovato"
              aria-label="Queue Jobs Table"
            />
          )}
          {activeTab === 'scadenze' && (
            <>
              {import.meta.env.MODE === 'development' && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Debug: {scadenzeReminders.length} scadenze trovate
                  </Typography>
                  {scadenzeReminders.length > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Prima scadenza: {JSON.stringify(scadenzeReminders[0], null, 2).slice(0, 200)}...
                    </Typography>
                  )}
                </Box>
              )}
              <DataTable
                data={sortedScadenzeReminders}
                columns={scadenzeColumns}
                loading={refreshing}
                emptyMessage="Nessuna scadenza con reminder trovata"
                aria-label="Scadenze Reminders Table"
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortOrder}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Job Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dettagli Job</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedJob.name}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" color="text.secondary">ID:</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedJob.id}
                  </Typography>
                </Box>
                
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" color="text.secondary">Stato:</Typography>
                  <QueueJobStatusChip status={selectedJob.status} />
                </Box>
                
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" color="text.secondary">Progresso:</Typography>
                  <Typography variant="body1">{selectedJob.progress || 0}%</Typography>
                </Box>
                
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" color="text.secondary">Tentativi:</Typography>
                  <Typography variant="body1">
                    {selectedJob.attemptsMade} / {selectedJob.opts?.attempts || 1}
                  </Typography>
                </Box>
                
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary">Dati Job:</Typography>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: 'background.default',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      maxHeight: 200
                    }}
                  >
                    {JSON.stringify(selectedJob.data, null, 2)}
                  </Box>
                </Box>
                
                {selectedJob.failedReason && (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Errore:</Typography>
                    <Box
                      sx={{
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        p: 2,
                        borderRadius: 1,
                        fontSize: '0.9rem'
                      }}
                    >
                      {selectedJob.failedReason.message}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueDashboard;
